import { NextRouter } from "next/router"
import {
  EventActorRef,
  InspirationRef,
  isEventActorRef,
  isInspirationRef,
  isPlaceRef,
  PlaceRef,
} from "@/types/entity"
import { TripTab, TripTabItinerary, TripTabs } from "@/types/trip"

import { PLACE_PATH_TYPES, routes } from "@/lib/routes"
import { tryParseUrl } from "@/lib/url-utils"
import { compact } from "./collections"
import { isChatPage, isExplorePageUrl } from "./page"
import { singleParam } from "./url"

export function isInspirationPage(router: NextRouter) {
  const currentURL = router.asPath
  return isInspirationPageUrl(currentURL)
}

export function isExplorePage(router: NextRouter) {
  const currentURL = router.asPath
  return isExplorePageUrl(currentURL)
}

export function isSavedPage(router: NextRouter) {
  const currentURL = router.asPath
  return isSavedPageUrl(currentURL)
}

export function isCollectionPage(router: NextRouter) {
  const currentURL = router.asPath
  return isCollectionPageUrl(currentURL)
}

export function isCreatePage(router: NextRouter) {
  const currentURL = router.asPath
  return isCreatePageUrl(currentURL)
}

export function isAdminPage(router: NextRouter) {
  const currentURL = router.asPath
  return isAdminPageUrl(currentURL)
}

export function isPlaceDetailPage(router: NextRouter) {
  const currentURL = router.asPath
  return isPlaceDetailPageUrl(currentURL)
}

// By Link
export function isInspirationUrl(urlStr: string) {
  const pathname = tryParseUrl(urlStr)?.pathname
  return pathname && isInspirationPageUrl(pathname)
}

export function isCollectionUrl(urlStr: string) {
  const pathname = tryParseUrl(urlStr)?.pathname
  return pathname && isCollectionPageUrl(pathname)
}

export function isInspirationPageUrl(url: string) {
  return routes.inspiration.test(url)
}

export function isCreatePageUrl(url: string) {
  return /^\/create(\?[^-]*)?$/.test(url)
}

export function isAdminPageUrl(url: string) {
  return routes.admin.test(url)
}

export function isFlightSearchPageUrl(url: string) {
  return /[?&]view=flight-search/.test(url)
}

export function isFlightBookingPageUrl(url: string) {
  return routes.flightsBooking.test(url)
}

export function isSavedPageUrl(url: string) {
  return routes.saved.test(url)
}

export function isCollectionPageUrl(url: string) {
  return routes.collection.test(url)
}

export function isProfilePageUrl(url: string) {
  return routes.profile.test(url)
}

/**
 * True only when `url` is this user's own profile page. A user with no handle
 * owns no profile page: comparing `match()?.handle` to an undefined handle
 * would otherwise be true for every non-profile path.
 */
export function isOwnProfilePageUrl(url: string, handle: string | undefined) {
  return !!handle && routes.profile.match(url)?.handle === handle
}

export function isSettingPageUrl(url: string) {
  return routes.settings.test(url)
}

export function isEventActorPageUrl(url: string) {
  return routes.eventActor.test(url)
}

export function isPlaceDetailPageUrl(url: string) {
  const [, first, rest] = url.split("/")
  return PLACE_PATH_TYPES.includes(first) && rest !== undefined
}

export function isAbsoluteUrl(url: string): boolean {
  return /^\w+:\/\//.test(url)
}

export function isExternalUrl(url: string): boolean {
  return (
    isAbsoluteUrl(url) && !/^\w+:\/\/(localhost|mindtrip\b.*\.ai)/.test(url)
  )
}

export function getPagePlaceParams(pathName: string): {
  name: string | undefined
  placeRef: PlaceRef | undefined
} {
  const queryParams = new URLSearchParams(pathName.split("?")[1])
  const ref = queryParams.get("ref")
  const placeRef = ref && isPlaceRef(ref) ? (ref as PlaceRef) : undefined

  return { name: undefined, placeRef }
}

export function placeRefFromDetailPageUrl(url: string): PlaceRef | undefined {
  const path = url.split("?")[0]
  if (!isPlaceDetailPageUrl(path)) return undefined
  const lastSegment = path.split("/").filter(Boolean).pop()
  return lastSegment && isPlaceRef(lastSegment) ? lastSegment : undefined
}

export function getPageInspirationParams(pathName: string): {
  name: string | undefined
  inspirationRef: InspirationRef | undefined
} {
  const queryParams = new URLSearchParams(pathName.split("?")[1])
  const ref = queryParams.get("ref")
  const inspirationRef =
    ref && isInspirationRef(ref) ? (ref as InspirationRef) : undefined

  return { name: undefined, inspirationRef }
}

export function getPageEventActorParams(pathName: string): {
  name: string | undefined
  eventActorRef: EventActorRef | undefined
} {
  const queryParams = new URLSearchParams(pathName.split("?")[1])
  const ref = queryParams.get("ref")
  const eventActorRef =
    ref && isEventActorRef(ref) ? (ref as EventActorRef) : undefined

  return { name: undefined, eventActorRef }
}

export const isTripTab = (v: unknown): v is TripTab => {
  return typeof v === "string" && TripTabs.includes(v)
}

export function getPageIsTripSheetOpenParams(query: URLSearchParams): {
  isOpen: boolean
  tab?: TripTab
} {
  const tripSheetValue = query.get("tripSheet")
  const tab = query.get("tripTab")

  return compact({
    isOpen: tripSheetValue === "1",
    tab: isTripTab(tab) ? tab : TripTabItinerary,
  })
}

export function getPageIsVerifyEmailOpenParams(router: NextRouter): {
  isVerify: boolean
} {
  const verifyEmailValue = router.query.verify
  if (verifyEmailValue) {
    if (verifyEmailValue === "1") {
      return { isVerify: true }
    }
  }
  return { isVerify: false }
}

export function getPageIsLocalTimeParams(router: NextRouter): boolean {
  return router.query.localTime === "1"
}

export function cleanUrlParams(
  router: NextRouter,
  paramsToRemove: string[],
  options?: { routeChatId?: number }
) {
  const cleanQuery = { ...router.query }
  paramsToRemove.forEach((param) => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete cleanQuery[param]
  })

  if (!singleParam(cleanQuery.id) && options?.routeChatId) {
    cleanQuery.id = String(options.routeChatId)
  }

  const cleanUrl = {
    pathname: router.pathname,
    query: cleanQuery,
  }

  void router.replace(cleanUrl, undefined, { shallow: true })
}

export { isChatPage }
